/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Render the student work board and its entries.
 * @state-owner navigation store owns filters; dataset store owns runs
 * @allowed-imports React; workspace and dataset stores; shared components, selectors and hooks
 * @forbidden teacher-only controls; direct API client
 * @public-api workspace main column
 * @side-effects navigation only
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { Fragment } from 'react';
import { FilterBar } from '../../../shared/components/FilterBar';
import { Page, PageHeader } from '../../../shared/components/PageShell';
import { type StatusGlyph, StatusIcon } from '../../../shared/components/StatusIcon';
import { Toolbar } from '../../../shared/components/VTable';
import { RUN_MODE_LABEL } from '../../../shared/lib/demo-data';
import { filterDefinitions } from '../../../shared/lib/filters';
import type { RunRecord } from '../../../shared/lib/model';
import {
  findActivity,
  myClassIndexes,
  myRuns,
  runName,
  type StudentRunState,
  studentRunState,
} from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';

const COLUMNS: readonly (readonly [StudentRunState, string, StatusGlyph])[] = [
  ['live', '進行中', 'live'],
  ['open', '還沒交', 'warn'],
  ['fix', '要訂正', 'err'],
  ['done', '已完成', 'done'],
];

export function StudentBoard() {
  const t = useTranslator();
  const classes = useDatasetStore((state) => state.classes);
  const runs = useDatasetStore((state) => state.runs);
  const filters = useWorkspaceUiStore((state) => state.filters.sact);
  const mine = myClassIndexes(classes);
  const active = filters?.f ?? [];
  const classFilter = filters?.ci ?? [];
  const rows = myRuns(classes, runs).filter(
    (run) =>
      (!active.length || active.includes(studentRunState(run))) &&
      (!classFilter.length || classFilter.includes(String(run.classIndex))),
  );

  return (
    <Page>
      <PageHeader
        meta={`${rows.length} ${t('項')} · ${mine.length} ${t('個班')}`}
        title={t('我的作業')}
      />
      <Toolbar
        left={
          <span className="vsumline">
            {COLUMNS.map(([state, label], index) => (
              <Fragment key={state}>
                {index > 0 ? <em>·</em> : null}
                <b>{rows.filter((run) => studentRunState(run) === state).length}</b>
                {` ${t(label)} `}
              </Fragment>
            ))}
          </span>
        }
      />
      <div className="vtbar">
        <FilterBar
          definitions={filterDefinitions('sact', classes, mine)}
          scope="sact"
          showDisplay={false}
        />
      </div>
      <div className="vbd">
        {COLUMNS.map(([state, label, glyph]) => {
          const group = rows.filter((run) => studentRunState(run) === state);
          return (
            <div className="vbc" key={state}>
              <div className="vbch">
                <StatusIcon glyph={glyph} />
                <b>{t(label)}</b>
                <span className="vbn">{group.length}</span>
              </div>
              {group.length ? (
                group.map((run) => <StudentRunCard key={run.id} run={run} />)
              ) : (
                <div className="vbe">{t('沒有')}</div>
              )}
            </div>
          );
        })}
      </div>
    </Page>
  );
}

function StudentRunCard({ run }: { run: RunRecord }) {
  const t = useTranslator();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const pick = useWorkspaceUiStore((state) => state.pick);
  const navigate = useWorkspaceUiStore((state) => state.navigate);
  const state = studentRunState(run);
  const activity = findActivity(activities, run.activityId);

  return (
    <button
      className="vcard"
      type="button"
      onClick={() => (state === 'done' || state === 'fix' ? navigate('rep', run.id) : pick(run.id))}
    >
      <div className="vch">
        <span className="vchip">{classes[run.classIndex].name}</span>
        <div className="sp" />
        {state === 'live' ? (
          <span className="vcpct">{t('進行中')}</span>
        ) : state === 'fix' ? (
          <span className="vcwarn">{t('要訂正')}</span>
        ) : state === 'done' ? (
          <StatusIcon glyph="done" />
        ) : (
          <span className="vcwarn">{t('還沒交')}</span>
        )}
      </div>
      <h4>{runName(activities, run)}</h4>
      {state === 'open' || state === 'live' ? (
        <span className="mtr" style={{ height: 3 }}>
          <i style={{ width: `${state === 'live' ? 45 : 20}%` }} />
        </span>
      ) : null}
      <div className="vcm">
        <span className="vcmi">{t(RUN_MODE_LABEL[run.mode])}</span>
        <span className="vcmi">{`${activity?.questionCount ?? 0} ${t('題')}`}</span>
        <div className="sp" />
        <span className="vcmi">
          {state === 'done' || state === 'fix' ? `92 ${t('分')}` : run.dateLabel}
        </span>
      </div>
    </button>
  );
}
